import React, { useState, useEffect } from 'react';
import { X, Save } from 'lucide-react';
import { supabase } from '../../../lib/supabase';
import { TestPrepLessonLog, TestPrepLessonPackage } from '../../../types';

interface LogLessonModalProps {
    isOpen: boolean;
    onClose: () => void;
    pkg: TestPrepLessonPackage | null;
    logToEdit?: TestPrepLessonLog | null;
    onSave: () => void;
}

const LogLessonModal: React.FC<LogLessonModalProps> = ({ isOpen, onClose, pkg, logToEdit, onSave }) => {
    const [loading, setLoading] = useState(false);

    // Form Data
    const [selectedItemId, setSelectedItemId] = useState<string>('');
    const [teacherName, setTeacherName] = useState('');
    const [lessonDate, setLessonDate] = useState('');
    const [lessonTime, setLessonTime] = useState('10:00');
    const [duration, setDuration] = useState('1'); // hours
    const [topic, setTopic] = useState('');
    const [notes, setNotes] = useState('');

    useEffect(() => {
        if (isOpen && pkg) {
            if (logToEdit) {
                const dt = new Date(logToEdit.lesson_date);
                setLessonDate(dt.toISOString().split('T')[0]);
                setLessonTime(dt.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' }));
                setTeacherName(logToEdit.teacher_name || '');
                setDuration(logToEdit.duration_hours?.toString() || '1');
                setTopic(logToEdit.topic || '');
                setNotes(logToEdit.notes || '');
                setSelectedItemId(logToEdit.item_id || '');
            } else {
                const now = new Date();
                setLessonDate(now.toISOString().split('T')[0]);
                setTeacherName('');
                setDuration('1');
                setTopic('');
                setNotes('');
                if (pkg.items && pkg.items.length > 0) {
                    setSelectedItemId(pkg.items[0].id || '');
                } else {
                    setSelectedItemId('');
                }
            }
        }
    }, [isOpen, pkg, logToEdit]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!pkg || !duration) return;

        setLoading(true);
        try {
            const dateTime = new Date(`${lessonDate}T${lessonTime}`).toISOString();
            const newDuration = Number(duration);

            if (logToEdit) {
                // EDIT EXISTING LOG
                const oldDuration = Number(logToEdit.duration_hours || 0);
                const durationDiff = newDuration - oldDuration;

                const { error: logError } = await supabase
                    .from('testprep_lesson_logs')
                    .update({
                        item_id: selectedItemId || null,
                        teacher_name: teacherName,
                        lesson_date: dateTime,
                        duration_hours: newDuration,
                        topic,
                        notes
                    })
                    .eq('id', logToEdit.id);

                if (logError) throw logError;

                if (durationDiff !== 0) {
                    let updatedItems = pkg.items ? [...pkg.items] : [];
                    if (selectedItemId && updatedItems.length > 0) {
                        updatedItems = updatedItems.map(item => {
                            if (item.id === selectedItemId) {
                                return {
                                    ...item,
                                    used_hours: Math.max(0, Number(item.used_hours || 0) + durationDiff)
                                };
                            }
                            return item;
                        });
                    }

                    const updatePayload: {
                        used_hours: number;
                        items?: TestPrepLessonPackage['items'];
                    } = {
                        used_hours: Math.max(0, Number(pkg.used_hours || 0) + durationDiff)
                    };

                    if (updatedItems.length > 0) {
                        updatePayload.items = updatedItems;
                    }

                    await supabase
                        .from('testprep_lesson_packages')
                        .update(updatePayload)
                        .eq('id', pkg.id);
                }
            } else {
                // INSERT NEW LOG
                const { error: logError } = await supabase
                    .from('testprep_lesson_logs')
                    .insert({
                        package_id: pkg.id,
                        item_id: selectedItemId || null,
                        teacher_name: teacherName,
                        lesson_date: dateTime,
                        duration_hours: newDuration,
                        topic,
                        notes,
                        status: 'completed'
                    });

                if (logError) throw logError;

                let updatedItems = pkg.items ? [...pkg.items] : [];
                if (selectedItemId && updatedItems.length > 0) {
                    updatedItems = updatedItems.map(item => {
                        if (item.id === selectedItemId) {
                            return {
                                ...item,
                                used_lessons: (item.used_lessons || 0) + 1,
                                used_hours: Number(item.used_hours || 0) + newDuration
                            };
                        }
                        return item;
                    });
                }

                const newUsedHours = Number(pkg.used_hours || 0) + newDuration;
                const newUsedLessons = (pkg.used_lessons || 0) + 1;
                
                const updatePayload: {
                    used_hours: number;
                    used_lessons: number;
                    items?: TestPrepLessonPackage['items'];
                } = {
                    used_hours: newUsedHours,
                    used_lessons: newUsedLessons
                };

                if (updatedItems.length > 0) {
                    updatePayload.items = updatedItems;
                }

                const { error: pkgError } = await supabase
                    .from('testprep_lesson_packages')
                    .update(updatePayload)
                    .eq('id', pkg.id);

                if (pkgError) throw pkgError;
            }

            onSave();
            onClose();
        } catch (error) {
            console.error('Error logging lesson:', error);
            alert('Ders işlenirken hata oluştu.');
        } finally {
            setLoading(false);
        }
    };

    if (!isOpen || !pkg) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white dark:bg-gray-800 rounded-xl w-full max-w-lg">
                <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
                    <div>
                        <h2 className="text-xl font-bold text-gray-900 dark:text-white">Ders Girişi</h2>
                        <p className="text-sm text-gray-500">{pkg.student?.first_name} {pkg.student?.last_name} - {pkg.subject}</p>
                    </div>
                    <button onClick={onClose} className="text-gray-500 hover:text-gray-700 dark:hover:text-gray-300">
                        <X className="w-6 h-6" />
                    </button>
                </div>

                <form onSubmit={handleSubmit} className="p-6 space-y-4">
                    {/* Branch Item Select */}
                    {pkg.items && pkg.items.length > 0 && (
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                Paket Branş / Dersi
                            </label>
                            <select
                                value={selectedItemId}
                                onChange={(e) => setSelectedItemId(e.target.value)}
                                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                            >
                                {pkg.items.map(item => (
                                    <option key={item.id} value={item.id}>
                                        {item.subject} (Kalan: {(item.total_lessons || 0) - (item.used_lessons || 0)} Ders)
                                    </option>
                                ))}
                            </select>
                        </div>
                    )}

                    {/* Teacher */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Dersi Veren Hoca
                        </label>
                        <input
                            type="text"
                            required
                            value={teacherName}
                            onChange={(e) => setTeacherName(e.target.value)}
                            placeholder="Ad Soyad"
                            className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                        />
                    </div>

                    {/* Date & Time */}
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                Tarih
                            </label>
                            <input
                                type="date"
                                required
                                value={lessonDate}
                                onChange={(e) => setLessonDate(e.target.value)}
                                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                Saat
                            </label>
                            <input
                                type="time"
                                required
                                value={lessonTime}
                                onChange={(e) => setLessonTime(e.target.value)}
                                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                            />
                        </div>
                    </div>

                    {/* Duration */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Süre (Saat)
                        </label>
                        <input
                            type="number"
                            required
                            min="0.5"
                            step="0.5"
                            value={duration}
                            onChange={(e) => setDuration(e.target.value)}
                            className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                        />
                    </div>

                    {/* Topic */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            İşlenen Konu
                        </label>
                        <input
                            type="text"
                            value={topic}
                            onChange={(e) => setTopic(e.target.value)}
                            className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                        />
                    </div>

                    {/* Notes */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Notlar
                        </label>
                        <textarea
                            rows={2}
                            value={notes}
                            onChange={(e) => setNotes(e.target.value)}
                            className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                        />
                    </div>

                    <div className="flex justify-end space-x-3 pt-4">
                        <button
                            type="button"
                            onClick={onClose}
                            className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                        >
                            İptal
                        </button>
                        <button
                            type="submit"
                            disabled={loading}
                            className="flex items-center space-x-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors disabled:opacity-50"
                        >
                            {loading ? (
                                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                            ) : (
                                <>
                                    <Save className="w-4 h-4" />
                                    <span>Kaydet</span>
                                </>
                            )}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default LogLessonModal;
